import api from './api.ts';

export interface DashboardStats {
  // Estadísticas del sistema principal
  system: {
    totalDocuments: number;
    pendingVerifications: number;
    completedSignatures: number;
    totalClients: number;
    systemHealth: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  };
  // Estadísticas de Adobe Sign
  adobeSign: {
    totalAgreements: number;
    signedAgreements: number;
    pendingAgreements: number;
    cancelledAgreements: number;
    systemHealth: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  };
  // Estado del webhook
  webhook: {
    status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
    lastEvent: string;
    eventCount: number;
  };
  // Autorización de Adobe Sign
  authorization: {
    authorized: boolean;
    needsReauthorization: boolean;
    lastTokenExpiry: string;
  };
  // Actividad reciente
  recentActivity: Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    timestamp: string;
    status: string;
    source: 'SYSTEM' | 'ADOBE_SIGN';
  }>;
  // Notificaciones
  notifications: Array<{
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    timestamp: string;
    source: 'SYSTEM' | 'ADOBE_SIGN';
  }>;
  lastUpdated: string;
}

export const dashboardApi = {
  // Obtener datos completos del dashboard unificado
  async getUnifiedDashboard(): Promise<DashboardStats> {
    try {
      // ✅ NUEVO: Usar endpoint real de estadísticas
      const [statsResponse, authStatusResponse] = await Promise.all([
        api.get('/admin/dashboard/stats'),
        api.get('/adobe-sign/auth/status')
      ]);

      const stats = statsResponse.data.data;

      const systemStats = {
        totalDocuments: stats.documentos.total,
        pendingVerifications: stats.documentos.pendientes,
        completedSignatures: stats.documentos.firmados,
        totalClients: stats.clientes.total,
        systemHealth: stats.documentos.pendientes > 50 ? 'WARNING' : stats.documentos.pendientes > 100 ? 'CRITICAL' : 'HEALTHY' as const
      };

      // Estadísticas de Adobe Sign
      const adobeSignStats = {
        totalAgreements: stats.adobeSign.totalAgreements,
        signedAgreements: stats.adobeSign.signedAgreements,
        pendingAgreements: stats.adobeSign.pendingAgreements,
        cancelledAgreements: 0, // Por implementar
        systemHealth: authStatusResponse.data.authorized ? 'HEALTHY' : 'CRITICAL' as const
      };

      // Estado del webhook
      const webhookStatus = {
        status: stats.webhook.status as 'ACTIVE' | 'INACTIVE',
        lastEvent: new Date().toISOString(),
        eventCount: stats.webhook.eventCount
      };

      // Autorización
      const authorization = {
        authorized: authStatusResponse.data.authorized,
        needsReauthorization: authStatusResponse.data.needsReauthorization,
        lastTokenExpiry: authStatusResponse.data.details?.lastTokenExpiry || ''
      };

      // Actividad reciente (combinada)
      const recentActivity = [
        // ✅ CORREGIDO: Usar datos de estadísticas en lugar de documents
        {
          id: 'system-stats',
          type: 'system_summary',
          title: 'Resumen del Sistema',
          description: `${stats.documentos.total} documentos, ${stats.documentos.firmados} firmados`,
          timestamp: new Date().toISOString(),
          status: 'ACTIVE',
          source: 'SYSTEM' as const
        },
        // Actividad de Adobe Sign
        ...(adobeSignStats.signedAgreements > 0 ? [{
          id: 'adobe-sign-1',
          type: 'agreement_signed',
          title: 'Documento firmado',
          description: `${adobeSignStats.signedAgreements} documentos firmados recientemente`,
          timestamp: new Date().toISOString(),
          status: 'completed',
          source: 'ADOBE_SIGN' as const
        }] : [])
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
       .slice(0, 5);

      // Notificaciones
      const notifications = [
        ...(authorization.needsReauthorization ? [{
          id: 'auth-warning',
          title: 'Autorización requerida',
          message: 'Adobe Sign requiere nueva autorización',
          type: 'warning' as const,
          timestamp: new Date().toISOString(),
          source: 'ADOBE_SIGN' as const
        }] : []),
        ...(stats.documentos.pendientes > 10 ? [{
          id: 'verifications-pending',
          title: 'Verificaciones pendientes',
          message: `${stats.documentos.pendientes} verificaciones esperando aprobación`,
          type: 'info' as const,
          timestamp: new Date().toISOString(),
          source: 'SYSTEM' as const
        }] : [])
      ];

      return {
        system: systemStats,
        adobeSign: adobeSignStats,
        webhook: webhookStatus,
        authorization,
        recentActivity,
        notifications,
        lastUpdated: new Date().toISOString()
      };

    } catch (error) {
      console.error('Error obteniendo datos del dashboard unificado:', error);
      throw error;
    }
  },

  // Obtener datos en tiempo real (para actualizaciones automáticas)
  async getRealTimeData() {
    try {
      const [authStatus, stats] = await Promise.all([
        api.get('/adobe-sign/auth/status'),
        api.get('/admin/dashboard/stats')
      ]);

      return {
        authorization: authStatus.data,
        pendingVerifications: stats.data.data?.documentos?.pendientes || 0
      };
    } catch (error) {
      console.error('Error obteniendo datos en tiempo real:', error);
      throw error;
    }
  }
};
